/**
 * ForestryFlight API functions
 * Handles flight plan CRUD operations and export functionality.
 */

import { api } from './index.js';

// Note: The forestry-flight endpoints are on the standalone Flight API (port 8095)
const FLIGHT_API_URL = import.meta.env.VITE_FLIGHT_API_URL || 'http://localhost:8095/api';

const flightApi = {
	get: (url, config) => api.get(url, { ...config, baseURL: FLIGHT_API_URL }),
	post: (url, data, config) => api.post(url, data, { ...config, baseURL: FLIGHT_API_URL }),
	put: (url, data, config) => api.put(url, data, { ...config, baseURL: FLIGHT_API_URL }),
	delete: (url, config) => api.delete(url, { ...config, baseURL: FLIGHT_API_URL }),
};

/**
 * Get all flight templates
 * @returns {Promise<Array>} List of flight templates
 */
export const getFlightTemplates = async () => {
	const { data } = await flightApi.get('/forestry-flight/templates');
	return data;
};

/**
 * Create a new flight template
 * @param {Object} templateData
 * @param {string} templateData.name - Template name (1-255 chars)
 * @param {string} [templateData.description] - Template description
 * @param {string} [templateData.template_type='orthomosaic'] - 'orthomosaic' or 'dsp'
 * @param {string} [templateData.scope='organization'] - 'organization' or 'user'
 * @param {string} [templateData.drone_model] - Drone model name (e.g. 'dji-matrice-4-enterprise')
 * @param {number} [templateData.flight_height=80] - Flight height in meters (20-500)
 * @param {number} [templateData.side_overlap=70] - Side overlap percentage (50-95)
 * @param {number} [templateData.front_overlap=75] - Front overlap percentage (50-95)
 * @param {number} [templateData.speed=8] - Flight speed in m/s (2-15)
 * @param {boolean} [templateData.terrain_follow=false]
 * @param {boolean} [templateData.smart_oblique=false]
 * @param {number} [templateData.gimbal_pitch=-90] - Gimbal pitch angle (-90 to 0)
 * @param {number} [templateData.flight_angle=0] - Flight angle (0-180)
 * @param {Object} [templateData.flight_settings] - Structured Ortho settings object
 * @param {Object} [templateData.dsp_settings] - Structured DSP settings (method, automatic_settings.point_count, distances, lens, etc.)
 * @param {boolean} [templateData.require_municipality=true] - Whether municipality is required
 * @param {string} [templateData.ai_model_uuid] - AI model UUID for auto-detection after processing
 * @param {boolean} [templateData.is_default=false] - Whether this is the user's default template
 * @returns {Promise<Object>} Created template with generated ID and timestamps
 */
export const createFlightTemplate = async templateData => {
	const { data } = await flightApi.post('/forestry-flight/templates', templateData);
	return data;
};

/**
 * Update an existing flight template
 * @param {string} templateId - The template UUID
 * @param {Object} updateData - Fields to update (same shape as create)
 * @returns {Promise<Object>} Updated template
 */
export const updateFlightTemplate = async (templateId, updateData) => {
	const { data } = await flightApi.put(`/forestry-flight/templates/${templateId}`, updateData);
	return data;
};

/**
 * Delete a flight template
 * @param {string} templateId - The template UUID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteFlightTemplate = async templateId => {
	const { data } = await flightApi.delete(`/forestry-flight/templates/${templateId}`);
	return data;
};

/**
 * Get all flight plans for the current user
 * @returns {Promise<Array>} List of flight plans
 */
export const getFlightPlans = async () => {
	const { data } = await flightApi.get('/forestry-flight/plans');
	return data;
};

/**
 * Get a specific flight plan by ID
 * @param {string} planId - The flight plan UUID
 * @returns {Promise<Object>} Flight plan details
 */
export const getFlightPlan = async planId => {
	const { data } = await flightApi.get(`/forestry-flight/plans/${planId}`);
	return data;
};

/**
 * Create a new flight plan
 * @param {Object} planData - Flight plan data
 * @param {string} planData.name - Plan name
 * @param {string} [planData.description] - Plan description
 * @param {string} planData.flight_type - 'orthomosaic' or 'dsp'
 * @param {string} [planData.drone_model] - Drone model name
 * @param {string} [planData.flight_template] - Flight template name
 * @param {Array<{lat: number, lng: number}>} planData.polygon_points - Polygon vertices
 * @param {Object} [planData.dsp_settings] - DSP configuration
 * @param {Array<{latitude: number, longitude: number, altitude?: number}>} [planData.waypoints] - DSP waypoints
 * @returns {Promise<Object>} Created flight plan
 */
export const createFlightPlan = async planData => {
	const { data } = await flightApi.post('/forestry-flight/plans', planData);
	return data;
};

/**
 * Update an existing flight plan
 * @param {string} planId - The flight plan UUID
 * @param {Object} updateData - Fields to update
 * @returns {Promise<Object>} Updated flight plan
 */
export const updateFlightPlan = async (planId, updateData) => {
	const { data } = await flightApi.put(`/forestry-flight/plans/${planId}`, updateData);
	return data;
};

/**
 * Delete a flight plan
 * @param {string} planId - The flight plan UUID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteFlightPlan = async planId => {
	const { data } = await flightApi.delete(`/forestry-flight/plans/${planId}`);
	return data;
};

/**
 * Export a flight plan to the specified format
 * @param {string} planId - The flight plan UUID
 * @param {string} format - Export format: 'kml', 'biodrone', or 'dji'
 * @returns {Promise<Object>} Export result (content for KML, confirmation for others)
 */
export const exportFlightPlan = async (planId, format = 'kml') => {
	const { data } = await flightApi.post(`/forestry-flight/plans/${planId}/export`, { format });
	return data;
};

/**
 * Download a flight plan as KML file
 * @param {string} planId - The flight plan UUID
 * @param {string} [filename] - Optional filename for download
 */
export const downloadFlightPlanKml = async (planId, filename) => {
	const result = await exportFlightPlan(planId, 'kml');

	// Create blob and download
	const blob = new Blob([result.content], { type: 'application/vnd.google-earth.kml+xml' });
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = filename || result.filename || 'flight_plan.kml';
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	window.URL.revokeObjectURL(url);
};
